import Dialog from "@mui/material/Dialog";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import CloseIcon from "@mui/icons-material/Close";
import { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import AppSnackbar from "layouts/notifications/Snackbar";
import { resetPassword } from "services/authService";
import { useTranslation } from "hooks/useTranslation";
import { useServerMessage } from "hooks/useServerMessage";

// Helper validation function

function validateForm(form, t) {
  const errors = {};

  if (!form.password) {
    errors.password = t(
      "superAdmin.modals.resetPasswordForm.validation.newPasswordRequired",
      "New Password is required"
    );
  }
  if (!form.confirm_password) {
    errors.confirm_password = t(
      "superAdmin.modals.resetPasswordForm.validation.confirmPasswordRequired",
      "Confirm Password is required"
    );
  }
  if (form.password && form.confirm_password && form.password !== form.confirm_password) {
    errors.confirm_password = t(
      "superAdmin.modals.resetPasswordForm.validation.passwordsDontMatch",
      "Passwords do not match"
    );
  }
  return errors;
}

export default function ResetPassword({ open, onClose, user, onSuccess }) {
  const { t } = useTranslation();
  const { tServer } = useServerMessage();
  const user_id = user ? user.id : null;
  const [form, setForm] = useState({
    password: "",
    confirm_password: "",
  });

  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Toast-snackbar
  // const [snackbar, setSnackbar] = useState({
  //   open: false,
  //   type: "",
  //   message: "",
  // });
  // const showSnackbar = (type, message) => {
  //   setSnackbar({ open: true, type, message });
  // };
  // const closeSnackbar = () => {
  //   setSnackbar((prev) => ({
  //     ...prev,
  //     open: false,
  //     type: "",
  //     message: "",
  //   }));
  // };
  const [snackbars, setSnackbars] = useState([]);

  const showSnackbar = (type, message) => {
    const id = Date.now() + Math.random(); // ensures unique key
    setSnackbars((prev) => [...prev, { id, type, message, open: true }]);

    // Auto close after 4 seconds
    setTimeout(() => closeSnackbar(id), 4000);
  };

  const closeSnackbar = (id) => {
    setSnackbars((prev) => prev.filter((snack) => snack.id !== id));
  };

  useEffect(() => {
    if (open) {
      setForm({
        password: "",
        confirm_password: "",
      });
      setErrors({});
      setShowPassword(false);
      setShowConfirmPassword(false);
    }
  }, [open]);

  const handleResetPassword = async () => {
    const validationErrors = validateForm(form, t);
    if (Object.keys(validationErrors).length === 0) {
      const result = await resetPasswordForm(form);
      if (result) {
        // Close modal after successful password reset
        setTimeout(() => {
          onClose();
          onSuccess?.();
        }, 1000); // Give time for snackbar to be visible
      }
    } else {
      setErrors(validationErrors);
      // Show only the first error to avoid multiple popups
      const firstError = Object.values(validationErrors)[0];
      showSnackbar("error", firstError);
    }
  };

  const handleCancel = () => {
    setForm({
      password: "",
      confirm_password: "",
    });
    setErrors({});
    onClose();
  };

  /* save user api call */
  async function resetPasswordForm(form) {
    let formObj = {
      user_id: user_id,
      new_password: form.password,
    };

    let result = await resetPassword(formObj);
    if (result.status == "success") {
      showSnackbar(
        "success",
        t("superAdmin.modals.messages.passwordReset", "Password reset successfully")
      );
      return true;
    } else {
      showSnackbar(
        "error",
        tServer(
          result.message,
          "superAdmin.modals.messages.resetFailed",
          "Failed to reset password"
        )
      );
      return false;
    }
  }

  return (
    <>
      <Dialog open={open} onClose={handleCancel} maxWidth="xs" fullWidth>
        <MDBox variant="gradient" borderRadius="lg" mb={1} position="relative" textAlign="center">
          <IconButton
            aria-label={t("common.aria.close", "close")}
            onClick={handleCancel}
            sx={{ position: "absolute", top: 8, right: 8 }}
          >
            <CloseIcon />
          </IconButton>
          <MDTypography variant="h4" fontWeight="medium" mt={3}>
            {t("superAdmin.modals.resetPassword", "Reset Password")}
          </MDTypography>
        </MDBox>
        <MDBox pb={3} px={5}>
          <MDBox component="form" role="form">
            <MDBox mb={4}>
              <MDInput
                type={showPassword ? "text" : "password"}
                name="password"
                label={t("superAdmin.modals.resetPasswordForm.labels.newPassword", "New Password")}
                variant="standard"
                value={form.password}
                fullWidth
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                error={Boolean(errors.password)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={t(
                          "common.aria.togglePasswordVisibility",
                          "toggle password visibility"
                        )}
                        onClick={() => setShowPassword((prev) => !prev)}
                        edge="end"
                        size="small"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </MDBox>
            <MDBox mb={4}>
              <MDInput
                type={showConfirmPassword ? "text" : "password"}
                name="confirm_password"
                label={t(
                  "superAdmin.modals.resetPasswordForm.labels.confirmNewPassword",
                  "Confirm New Password"
                )}
                variant="standard"
                value={form.confirm_password}
                fullWidth
                onChange={(e) => setForm({ ...form, confirm_password: e.target.value })}
                error={Boolean(errors.confirm_password)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={t(
                          "common.aria.toggleConfirmPasswordVisibility",
                          "toggle confirm password visibility"
                        )}
                        onClick={() => setShowConfirmPassword((prev) => !prev)}
                        edge="end"
                        size="small"
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </MDBox>
            <MDBox mt={3} mb={1} textAlign="center">
              <MDButton variant="gradient" color="info" onClick={handleResetPassword}>
                {t("superAdmin.modals.resetPasswordForm.buttons.reset", "Reset")}
              </MDButton>
            </MDBox>
          </MDBox>
        </MDBox>
      </Dialog>
      {ReactDOM.createPortal(
        snackbars.map((snack) => (
          <AppSnackbar
            key={snack.id}
            type={snack.type}
            message={snack.message}
            open={snack.open}
            onClose={() => closeSnackbar(snack.id)}
          />
        )),
        document.body
      )}
    </>
  );
}

import PropTypes from "prop-types";

ResetPassword.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  user: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onSuccess: PropTypes.func.isRequired,
};
